const UNRESOLVED_TOKEN_REGEX = /\[(MANUAL|OPTIONAL|CAPTION):\s*[^\]]+\]/gi;
const PLACEHOLDER_LINE_PATTERNS = [
  /^add content for\b/i,
  /^aggiungi contenuto per\b/i,
  /^añadir contenido para\b/i,
  /^anadir contenido para\b/i,
];
const INTERNAL_SECTION_TITLES = new Set([
  'data gaps',
  'template metadata',
  'lacune dati',
  'dati mancanti',
  'metadati template',
]);
const DEFAULT_TOC_TITLE_BY_LOCALE = {
  it: 'Indice',
  es: 'Indice',
  en: 'Index',
};
const TOC_HEADING_TITLE_ALIASES = new Set([
  'indice',
  'index',
  'table of contents',
  'sommario',
]);
const NON_EMPTY_NODE_TYPES = new Set([
  'report-cover',
  'report-visual',
  'table',
  'tablerow',
  'tablecell',
  'table-of-contents',
]);

const normalizeComparableText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();

const cleanTokenizedText = (value) => String(value || '')
  .replace(UNRESOLVED_TOKEN_REGEX, '')
  .replace(/\s{2,}/g, ' ')
  .trim();

const isPlaceholderLine = (value) => {
  const normalized = cleanTokenizedText(value);
  if (!normalized) {
    return true;
  }
  return PLACEHOLDER_LINE_PATTERNS.some(pattern => pattern.test(normalized));
};

const sanitizeTextValue = (value) => {
  const cleaned = cleanTokenizedText(value);
  if (!cleaned || isPlaceholderLine(cleaned)) {
    return '';
  }
  return cleaned;
};

const sanitizeRows = (rows) => {
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows
    .map((row) => ({
      label: sanitizeTextValue(row?.label),
      value: sanitizeTextValue(row?.value),
    }))
    .filter(row => row.label || row.value);
};

const getNodeText = (node) => {
  if (!node || typeof node !== 'object') {
    return '';
  }
  const parts = [];
  if (typeof node.text === 'string') {
    parts.push(node.text);
  }
  if (Array.isArray(node.children)) {
    node.children.forEach((child) => {
      const text = getNodeText(child);
      if (text) {
        parts.push(text);
      }
    });
  }
  return parts.join(' ').replace(/\s+/g, ' ').trim();
};

const getHeadingLevel = (node) => {
  if (!node || (node.type !== 'heading' && node.type !== 'heading-anchor')) {
    return null;
  }
  const numericLevel = Number(String(node.tag || '').replace('h', ''));
  if (Number.isNaN(numericLevel)) {
    return 1;
  }
  return Math.max(1, Math.min(6, numericLevel));
};

const shouldDropAsEmptyNode = (node) => {
  if (!node || typeof node !== 'object') {
    return true;
  }
  if (NON_EMPTY_NODE_TYPES.has(node.type)) {
    return false;
  }
  if (typeof node.text === 'string' && node.text.trim()) {
    return false;
  }
  if (Array.isArray(node.children) && node.children.length > 0) {
    return false;
  }
  return true;
};

const sanitizeNode = (node) => {
  if (!node || typeof node !== 'object') {
    return null;
  }

  if (node.type === 'placeholder') {
    return null;
  }

  if (node.type === 'text') {
    const text = sanitizeTextValue(node.text);
    if (!text) {
      return null;
    }
    return {
      ...node,
      text,
    };
  }

  if (node.type === 'report-cover') {
    const data = node.data || {};
    return {
      ...node,
      data: {
        ...data,
        title: sanitizeTextValue(data.title),
        subtitle: sanitizeTextValue(data.subtitle),
        referenceLine: sanitizeTextValue(data.referenceLine),
        client: sanitizeTextValue(data.client),
        date: sanitizeTextValue(data.date),
        metadataRows: sanitizeRows(data.metadataRows),
        essentialRows: sanitizeRows(data.essentialRows),
        extendedRows: sanitizeRows(data.extendedRows),
        metadataSectionTitle: sanitizeTextValue(data.metadataSectionTitle),
      },
    };
  }

  if (node.type === 'report-visual') {
    const data = node.data || {};
    const imageList = Array.isArray(data.imageList)
      ? data.imageList
        .map(image => ({
          ...image,
          caption: sanitizeTextValue(image?.caption),
          alt: sanitizeTextValue(image?.alt),
        }))
        .filter(image => image.url || image.caption || image.alt)
      : [];
    return {
      ...node,
      data: {
        ...data,
        title: sanitizeTextValue(data.title),
        caption: sanitizeTextValue(data.caption),
        imageList,
      },
    };
  }

  const nextNode = { ...node };
  if (typeof nextNode.text === 'string') {
    nextNode.text = sanitizeTextValue(nextNode.text);
  }

  if (Array.isArray(nextNode.children)) {
    nextNode.children = nextNode.children
      .map(sanitizeNode)
      .filter(Boolean);
  }

  if (shouldDropAsEmptyNode(nextNode)) {
    return null;
  }
  return nextNode;
};

const sanitizeTopLevelChildren = (children, options = {}) => {
  const {
    maxTocLevel = null,
    removeInternalSections = true,
  } = options;
  const sanitized = [];
  let skipUntilLevel = null;

  children.forEach((rawChild) => {
    const child = sanitizeNode(rawChild);
    if (!child) {
      return;
    }

    const headingLevel = getHeadingLevel(child);
    const isHeading = headingLevel != null;

    if (skipUntilLevel != null) {
      if (isHeading && headingLevel <= skipUntilLevel) {
        skipUntilLevel = null;
      } else {
        return;
      }
    }

    if (child.type === 'table-of-contents' && Number.isFinite(maxTocLevel)) {
      child.maxDepth = maxTocLevel;
    }

    if (isHeading) {
      const headingText = getNodeText(child);
      if (!headingText) {
        return;
      }
      if (removeInternalSections && INTERNAL_SECTION_TITLES.has(normalizeComparableText(headingText))) {
        skipUntilLevel = headingLevel;
        return;
      }
    }

    sanitized.push(child);
  });

  return sanitized;
};

const parseSerializedState = (serializedState) => {
  if (!serializedState) {
    return null;
  }
  if (typeof serializedState === 'string') {
    try {
      return JSON.parse(serializedState);
    } catch (error) {
      return null;
    }
  }
  if (typeof serializedState === 'object') {
    return serializedState;
  }
  return null;
};

const createSerializedTextNode = (text) => ({
  detail: 0,
  format: 0,
  mode: 'normal',
  style: '',
  text: String(text || ''),
  type: 'text',
  version: 1,
});

const createSerializedTocHeadingNode = (title) => ({
  type: 'heading-anchor',
  version: 1,
  tag: 'h1',
  anchorId: 'toc',
  format: '',
  indent: 0,
  direction: null,
  children: [createSerializedTextNode(title)],
});

const buildSanitizedCoverNode = (coverBlock = {}) => ({
  type: 'report-cover',
  version: 1,
  data: {
    ...coverBlock,
    title: sanitizeTextValue(coverBlock.title || coverBlock.lines?.[0]),
    subtitle: sanitizeTextValue(coverBlock.subtitle || coverBlock.lines?.[1]),
    referenceLine: sanitizeTextValue(coverBlock.referenceLine || coverBlock.lines?.[2]),
    client: sanitizeTextValue(coverBlock.client),
    date: sanitizeTextValue(coverBlock.date),
    metadataRows: sanitizeRows(coverBlock.essentialRows || coverBlock.metadataRows || []),
    essentialRows: sanitizeRows(coverBlock.essentialRows || coverBlock.metadataRows || []),
    extendedRows: sanitizeRows(coverBlock.extendedRows || []),
    metadataSectionTitle: sanitizeTextValue(coverBlock.metadataSectionTitle),
  },
});

const getHeadingAnchorId = (node) => {
  const rawId = node?.anchorId || node?.id || '';
  return String(rawId || '').trim().toLowerCase();
};

const isHeadingLikeNode = (node) => (
  node?.type === 'heading' || node?.type === 'heading-anchor'
);

const isTocHeadingNode = (node, candidates) => {
  if (!isHeadingLikeNode(node)) {
    return false;
  }
  if (getHeadingAnchorId(node) === 'toc') {
    return true;
  }
  const headingText = normalizeComparableText(getNodeText(node));
  if (!headingText) {
    return false;
  }
  return candidates.has(headingText);
};

const getFirstTemplateCoverBlock = (templateDocument) => (
  (templateDocument?.sections || [])
    .flatMap(section => section?.blocks || [])
    .find(block => block?.type === 'cover') || null
);

const resolveTocHeadingTitle = (locale, explicitTitle) => {
  const normalizedLocale = String(locale || 'it').toLowerCase().slice(0, 2);
  const fallback = DEFAULT_TOC_TITLE_BY_LOCALE[normalizedLocale]
    || DEFAULT_TOC_TITLE_BY_LOCALE.it;
  const title = sanitizeTextValue(explicitTitle || fallback);
  return title || fallback;
};

const buildTocHeadingTitleCandidates = (locale, explicitTitle) => {
  const title = resolveTocHeadingTitle(locale, explicitTitle);
  const candidates = new Set(TOC_HEADING_TITLE_ALIASES);
  candidates.add(normalizeComparableText(title));
  return {
    title,
    candidates,
  };
};

const normalizeOfficialStructure = (children, locale, options = {}) => {
  const sourceChildren = Array.isArray(children) ? children : [];
  const {
    tocHeadingTitle = null,
    templateCoverBlock = null,
    templateDocument = null,
    maxTocLevel = null,
    includeToc = true,
  } = options;
  const { title: resolvedTocTitle, candidates: tocTitleCandidates } = buildTocHeadingTitleCandidates(
    locale,
    tocHeadingTitle
  );

  const coverIndices = [];
  const tocHeadingIndices = [];
  const tocBlockIndices = [];

  sourceChildren.forEach((node, index) => {
    if (node?.type === 'report-cover') {
      coverIndices.push(index);
      return;
    }
    if (isTocHeadingNode(node, tocTitleCandidates)) {
      tocHeadingIndices.push(index);
      return;
    }
    if (node?.type === 'table-of-contents') {
      tocBlockIndices.push(index);
    }
  });

  const fallbackCoverBlock = templateCoverBlock || getFirstTemplateCoverBlock(templateDocument);
  const primaryCoverNode = coverIndices.length > 0
    ? sourceChildren[coverIndices[0]]
    : (fallbackCoverBlock ? buildSanitizedCoverNode(fallbackCoverBlock) : null);
  const primaryTocHeadingNode = tocHeadingIndices.length > 0
    ? sourceChildren[tocHeadingIndices[0]]
    : (includeToc ? createSerializedTocHeadingNode(resolvedTocTitle) : null);
  const primaryTocBlockNode = tocBlockIndices.length > 0
    ? sourceChildren[tocBlockIndices[0]]
    : (includeToc ? {
      type: 'table-of-contents',
      version: 1,
      ...(Number.isFinite(maxTocLevel) ? { maxDepth: maxTocLevel } : {}),
    } : null);

  const excludedIndexes = new Set([
    ...coverIndices,
    ...tocHeadingIndices,
    ...tocBlockIndices,
  ]);
  const remainingNodes = sourceChildren.filter((_, index) => !excludedIndexes.has(index));

  return [
    ...(primaryCoverNode ? [primaryCoverNode] : []),
    ...(primaryTocHeadingNode ? [primaryTocHeadingNode] : []),
    ...(primaryTocBlockNode ? [primaryTocBlockNode] : []),
    ...remainingNodes,
  ];
};

export const sanitizeSerializedStateForOfficialExport = (
  serializedState,
  locale = 'it',
  options = {}
) => {
  const parsed = parseSerializedState(serializedState);
  if (!parsed?.root || !Array.isArray(parsed.root.children)) {
    return typeof serializedState === 'string' ? serializedState : null;
  }

  const tocDepth = Number.isFinite(options.maxTocLevel) ? options.maxTocLevel : null;
  const sanitizedRootChildren = sanitizeTopLevelChildren(parsed.root.children, {
    maxTocLevel: tocDepth,
    removeInternalSections: options.removeInternalSections !== false,
    locale,
  });
  const cleanedRootChildren = options.normalizeStructure
    ? normalizeOfficialStructure(sanitizedRootChildren, locale, {
      tocHeadingTitle: options.tocHeadingTitle,
      templateCoverBlock: options.templateCoverBlock,
      templateDocument: options.templateDocument,
      maxTocLevel: tocDepth,
      includeToc: options.includeToc !== false,
    })
    : sanitizedRootChildren;
  const nextState = {
    ...parsed,
    root: {
      ...parsed.root,
      children: cleanedRootChildren,
    },
  };

  if (typeof serializedState === 'string') {
    try {
      return JSON.stringify(nextState);
    } catch (error) {
      return serializedState;
    }
  }
  return nextState;
};
